import { Head, usePage } from '@inertiajs/react';
import { ChevronDown, LoaderCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

import OrganizerLoginController from '@/actions/App/Http/Controllers/Organizer/Auth/LoginController';
import ForgotPasswordInertiaController from '@/actions/App/Http/Controllers/User/Auth/ForgotPasswordInertiaController';
import LoginInertiaController from '@/actions/App/Http/Controllers/User/Auth/LoginInertiaController';
import RegisterInertiaController from '@/actions/App/Http/Controllers/User/Auth/RegisterInertiaController';
import AuthLayout from '@/components/ctv/layouts/auth-layout';
import { FormInput } from '@/components/form-input/form-input';
import SocialLoginButtons from '@/components/social-login-buttons';
import TextLink from '@/components/text-link';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Form } from '@/components/ui/form';
import { post } from '@/lib/axios';
import { safeRedirectParam } from '@/lib/redirect';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm as useHookForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

type LoginForm = {
  identifier: string;
  password: string;
};

interface LoginProps {
  status?: string;
  canResetPassword: boolean;
  pageTitle: string;
  bgImage: string;
  registration: boolean;
}

const formSchema = z.object({
  identifier: z.string().min(1, { message: 'Email hoặc số điện thoại là bắt buộc' }),
  password: z.string().min(1, { message: 'Mật khẩu là bắt buộc' }),
});

export default function Login({ status, pageTitle, bgImage, registration }: LoginProps) {
  const [processing, setProcessing] = useState(false);
  // Manual (mobile/email) login is a disclosure — social providers lead the page.
  const [manualOpen, setManualOpen] = useState(false);
  const flash = usePage().props.flash as [string, string] | undefined;

  // Social login failures redirect back here with a notify tuple; surface it.
  useEffect(() => {
    if (flash?.[0] === 'error') {
      toast.error(flash[1]);
    }
  }, [flash]);

  const form = useHookForm<LoginForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      identifier: '',
      password: '',
    },
  });

  const handleSubmit = async (data: LoginForm) => {
    setProcessing(true);

    await post(LoginInertiaController.login().url, data)
      .then((response) => {
        if (response?.data?.success) {
          toast.success('Đăng nhập thành công');
          form.reset();
          window.location.href = safeRedirectParam() ?? response.data.redirect_url;
        } else {
          // Failed login resolves (not throws): axios validateStatus accepts 422,
          // and the controller returns a bare `{ error }` body with no `data` key.
          const failure = response as unknown as { error?: string; message?: string };
          toast.error(failure?.error || failure?.message || 'Email/số điện thoại hoặc mật khẩu không đúng');
        }
      })
      .catch((error) => {
        toast.error((error as { message?: string })?.message || 'Email/số điện thoại hoặc mật khẩu không đúng');
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  return (
    <AuthLayout title={pageTitle} bgImage={bgImage} description={''}>
      <Head title={pageTitle} />

      <div className="flex flex-col gap-4">
        {/* Social provider rows lead the page — the disclosure trigger below acts as the separator */}
        <SocialLoginButtons redirectTo={safeRedirectParam() ?? undefined} showDivider={false} />

        {/* Manual login disclosure — mirrors components/web/auth/login-dialog.tsx */}
        <Collapsible open={manualOpen} onOpenChange={setManualOpen}>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="flex w-full items-center justify-center gap-1.5 text-sm font-medium whitespace-nowrap text-muted-foreground hover:bg-transparent hover:text-foreground"
            >
              Lựa chọn khác
              <ChevronDown
                className="size-4 transition-transform duration-200 motion-reduce:transition-none data-[state=open]:rotate-180"
                data-state={manualOpen ? 'open' : 'closed'}
              />
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent className="animate-in fade-in-0 slide-in-from-top-2 duration-200 motion-reduce:animate-none">
            <Form {...form}>
              <form className="mt-4 flex flex-col gap-6" onSubmit={form.handleSubmit(handleSubmit)}>
                <div className="grid gap-6">
                  <div className="grid gap-2">
                    <FormInput
                      form={form}
                      name="identifier"
                      label="Email hoặc số điện thoại"
                      placeholder="Email hoặc số điện thoại"
                      isRequired
                      id="identifier"
                      autoComplete="username"
                    />
                  </div>

                  <div className="grid gap-2">
                    <FormInput form={form} type="password" name="password" label="Mật khẩu" placeholder="Mật khẩu" isRequired id="password" />
                  </div>

                  <div className="flex items-center justify-end">
                    <TextLink
                      className="text-sm text-blue-600 hover:text-blue-800"
                      href={ForgotPasswordInertiaController.showLinkRequestForm().url}
                    >
                      Quên mật khẩu?
                    </TextLink>
                  </div>

                  <Button type="submit" className="mt-4 w-full" tabIndex={4} disabled={processing}>
                    {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                    Đăng nhập
                  </Button>
                </div>

                {registration && (
                  <div className="text-muted-foreground text-center text-sm">
                    Chưa có tài khoản?{' '}
                    <TextLink className="font-semibold" href={RegisterInertiaController.showRegistrationForm().url} tabIndex={5}>
                      Tạo tài khoản
                    </TextLink>
                  </div>
                )}

                <div className="text-center">
                  <TextLink className="text-sm text-blue-600 hover:text-blue-800" href={OrganizerLoginController.showLoginFormInertia().url}>
                    Đăng nhập bằng tài khoản đối tác
                  </TextLink>
                </div>
              </form>
            </Form>
          </CollapsibleContent>
        </Collapsible>

        {status && <div className="text-center text-sm font-medium text-green-600">{status}</div>}
      </div>
    </AuthLayout>
  );
}
